/*
 * Where a person's notifications go — on the profile page. Decision A of `DESIGN-teams.md`
 * (each person chooses), and since Slack build step 2 decision C1 of `DESIGN-slack.md`:
 * **tick boxes, Email / Teams / Slack, at least one.** (Until 13/10/2026 this was three radio
 * buttons: Email / Teams / Both.)
 *
 * ⚠️ **IT IS THEIR INBOX, WHICH IS WHY THIS IS HERE AND NOT IN SETTINGS.** A company-wide
 * switch would either double-notify everybody who hates duplicates or silence the people
 * who still live in email.
 *
 * ⚠️ **THERE IS NO "NOTHING" AND THERE MUST NEVER BE ONE.** Rob ruled it out on 24/08/2026:
 * an approver who silences themselves becomes a bottleneck nobody can see. So the last tick
 * can't be taken off.
 *
 * ⚠️ **A CHAT APP ONLY APPEARS ONCE THE COMPANY USES IT, AND CAN'T BE YOUR ONLY TICK UNTIL IT
 * CAN REACH YOU.** Slack: the company has connected it, and the box stays greyed until we've
 * found you in it (the v1 mock-up Rob answered C1 on). Teams: somebody in the company has
 * added the app; it can be ticked beside email before you add it yourself, but not alone.
 * The rules are `lib/notificationChannels.js`; the server enforces the same ones
 * (`services/channel_reach.refusal`) — this card is the polite half, not the enforcement.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Mail, MessageSquare, Hash, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { profileApi, apiErrorMessage } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import {
  asStored, parseChannels, reachableChats, refusal, shownChannels, toggled,
} from '../lib/notificationChannels';

/*
 * ⚠️ **THE ONLY PLACE A CHANNEL IS NAMED, ON PURPOSE.** A `channel === 'teams' ? … : …`
 * scattered through the rendering is how the rows start quietly diverging — and it would be
 * the least-tested one that ended up wrong.
 */
const CHANNELS = [
  { id: 'email', label: 'Email', icon: Mail },
  { id: 'teams', label: 'Microsoft Teams', icon: MessageSquare },
  { id: 'slack', label: 'Slack', icon: Hash },
];

/* Fired by the sign-in offer bar when it turns Slack on, so an open card catches up. */
export const CHANNELS_CHANGED = 'hh:notification-channels-changed';

const ago = (iso) => formatDistanceToNow(new Date(iso), { addSuffix: true });

export default function NotificationChannelCard() {
  const { user } = useAuth();
  /*
   * ⚠️ **THE SAME FOUR-WAY CHECK SETTINGS USES.** `role` arrives lower-case from `/auth/me`
   * and upper-case from some older payloads; getting it wrong sends an administrator the
   * "ask your administrator" sentence — a small, permanent, baffling dead end.
   */
  const isAdmin = ['admin', 'superadmin', 'ADMIN', 'SUPERADMIN'].includes(user?.role);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => profileApi
    .getNotifications()
    .then((res) => setSettings(res.data))
    .catch(() => setSettings(null))
    .finally(() => setLoading(false)), []);

  useEffect(() => {
    load();
    window.addEventListener(CHANNELS_CHANGED, load);
    return () => window.removeEventListener(CHANNELS_CHANGED, load);
  }, [load]);

  const ticks = settings ? (settings.channels?.length ? settings.channels
    : parseChannels(settings.notification_channel)) : [];

  const choose = async (id) => {
    if (!settings || saving) return;
    const next = toggled(ticks, id);
    const why = refusal(ticks, next, settings);
    if (why) { toast.error(why); return; }

    /*
     * ⚠️ **THE PREVIOUS VALUE IS KEPT SO A FAILED SAVE CAN BE PUT BACK.** Without this the
     * box stays where the person clicked while the server still holds the old value — the
     * screen then confidently shows a setting that is not in force. A wrong answer is worse
     * than a slow one.
     */
    const previous = settings;
    const stored = asStored(next);
    setSettings({ ...settings, notification_channel: stored, channels: next });
    setSaving(true);
    try {
      await profileApi.update({ notification_channel: stored });
      toast.success('Notification preference saved.');
    } catch (e) {
      setSettings(previous);
      toast.error(apiErrorMessage(e, 'Couldn’t save that. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  /*
   * "Was this you?" on a new device (DESIGN-security.md step 2, Rob's 3A): on for everyone,
   * and this is where each person turns it off. It goes wherever the ticks above say.
   */
  const toggleNewDevice = async () => {
    if (!settings || saving) return;
    const previous = settings;
    const next = settings.new_device_alerts === false;
    setSettings({ ...settings, new_device_alerts: next });
    setSaving(true);
    try {
      await profileApi.update({ new_device_alerts: next });
      toast.success(next ? 'We’ll tell you when you sign in on a new device.'
        : 'We won’t tell you about new devices any more.');
    } catch (e) {
      setSettings(previous);
      toast.error(apiErrorMessage(e, 'Couldn’t save that. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="card-hive p-6" data-testid="notification-channel-card">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!settings) {
    /*
     * ⚠️ Says what is unknown rather than pretending everything is fine. An empty card where
     * a setting should be reads as "there is no such setting".
     */
    return (
      <div className="card-hive p-6" data-testid="notification-channel-card">
        <p className="font-medium text-sm">Where we send your notifications</p>
        <p className="text-xs text-muted-foreground mt-1">
          We couldn’t load this just now. Refresh the page to try again — your existing
          preference hasn’t changed.
        </p>
      </div>
    );
  }

  const reach = reachableChats(settings);
  const team = settings.slack_team_name ? `${settings.slack_team_name}’s Slack` : 'your company’s Slack';
  const shown = shownChannels(settings, ticks);
  const lastTick = ticks.length === 1 ? ticks[0] : null;
  /* With K1, email is what reaches them while Slack can't — unless another chat app does. */
  const emailCovers = ticks.includes('email') || !ticks.some((t) => reach.includes(t));

  /* One line under each box: what it is, or why it can't be chosen. */
  const detail = (id) => {
    if (id === 'email') {
      return settings.email
        ? settings.email
        /*
         * ⚠️ An employee with NO email address is a supported state since 21/07/2026.
         * Saying "Email" with nothing beside it would look like a loading bug rather than
         * the real fact that email cannot reach them.
         */
        : 'You have no email address on your profile, so this won’t reach you';
    }
    if (id === 'teams') {
      return settings.teams_installed
        ? 'A message from the HolidayHive bot'
        : 'Add the HolidayHive app in Teams to use this on its own';
    }
    return settings.slack_found
      ? `A direct message from HolidayHive in ${team}`
      : 'Not available yet';
  };

  return (
    <div className="card-hive p-6" data-testid="notification-channel-card">
      <p className="font-medium text-sm">Where we send your notifications</p>
      <p className="text-xs text-muted-foreground mt-1 max-w-md">
        Leave requests, approvals, reminders, the who&rsquo;s-off digests and birthday
        reminders. Tick as many as you like, but at least one.
      </p>

      <div className="mt-4 space-y-2">
        {CHANNELS.filter((c) => shown.includes(c.id)).map((channel) => {
          const Icon = channel.icon;
          const on = ticks.includes(channel.id);
          const blockedBy = refusal(ticks, toggled(ticks, channel.id), settings);
          /*
           * ⚠️ **DISABLED, WITH THE REASON UNDERNEATH — NEVER A BOX THAT REFUSES ON CLICK
           * WITH NO EXPLANATION.** The line below the list says why.
           */
          const disabled = saving || !!blockedBy;
          return (
            <label
              key={channel.id}
              data-testid={`notification-channel-${channel.id}`}
              className={`flex items-start gap-3 p-3 rounded-md border ${on
                ? 'border-primary ring-1 ring-primary' : 'border-border'} ${disabled && !on
                ? 'opacity-60' : ''} ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 accent-primary"
                checked={on}
                disabled={disabled}
                onChange={() => choose(channel.id)}
                data-testid={`notification-channel-${channel.id}-box`}
              />
              <span className="min-w-0">
                <span className="text-sm flex items-center gap-2">
                  <Icon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                  {channel.label}
                </span>
                <span className="block text-xs text-muted-foreground mt-0.5">{detail(channel.id)}</span>
              </span>
            </label>
          );
        })}
      </div>

      {lastTick && (
        <p className="text-xs text-muted-foreground mt-2" data-testid="notification-last-tick">
          {CHANNELS.find((c) => c.id === lastTick)?.label} is your only tick, so it can’t be
          taken off until you tick another.
        </p>
      )}
      {!lastTick && ticks.length > 1 && !ticks.includes('email') && ticks.filter((c) => reach.includes(c)).length === 1 && (
        <p className="text-xs text-muted-foreground mt-2" data-testid="notification-only-reach">
          {CHANNELS.find((c) => c.id === ticks.find((t) => reach.includes(t)))?.label} is the only
          one that can reach you at the moment, so it can’t be taken off.
        </p>
      )}

      {/* ── Teams ─────────────────────────────────────────────────────────────────── */}
      {shown.includes('teams') && settings.teams_installed && (
        <p className="text-xs text-emerald-700 mt-3 flex items-center gap-1" data-testid="notification-teams-status">
          <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
          {/*
            * ⚠️ NULL means "we have no record of sending", never "nothing has been sent".
            */}
          Connected to Teams
          {settings.teams_display_name ? ` as ${settings.teams_display_name}` : ''}
          {settings.teams_last_sent_at ? ` · last message ${ago(settings.teams_last_sent_at)}` : ' · nothing sent there yet'}
        </p>
      )}
      {shown.includes('teams') && !settings.teams_installed && (
        <div className="flex items-start gap-2 mt-3 p-3 rounded-md bg-amber-50 border border-amber-200"
             data-testid="notification-teams-not-installed">
          <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" aria-hidden="true" />
          {/*
            * ⚠️ **AN EMPLOYEE IS NOT SENT TO A PAGE THEY CANNOT OPEN** (Rob, 24/08/2026).
            * Settings is admin-only, so they are told who to ask instead.
            */}
          {isAdmin ? (
            <p className="text-xs text-amber-800">
              To get these in Teams, add the HolidayHive app in Microsoft Teams.{' '}
              <Link to="/settings?tab=integrations" className="underline font-medium"
                    data-testid="notification-teams-setup-link">
                How, in Settings &rarr; Integrations
              </Link>
              .
            </p>
          ) : (
            <p className="text-xs text-amber-800">
              To get these in Teams on their own, add the HolidayHive app in Microsoft Teams —
              ask whoever administers HolidayHive if you’re not sure how. Until then we have no
              way to message you there.
            </p>
          )}
        </div>
      )}

      {/* ── Slack ─────────────────────────────────────────────────────────────────── */}
      {shown.includes('slack') && settings.slack_found && (
        <p className="text-xs text-emerald-700 mt-3 flex items-center gap-1" data-testid="notification-slack-status">
          <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
          Found you in Slack{settings.slack_name ? ` as ${settings.slack_name}` : ''}
          {settings.slack_last_sent_at ? ` · last message ${ago(settings.slack_last_sent_at)}` : ' · nothing sent there yet'}
        </p>
      )}
      {shown.includes('slack') && settings.slack_found && settings.slack_last_error && ticks.includes('slack') && (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2 mt-2"
           data-testid="notification-slack-error">
          Slack’s last answer was an error ({settings.slack_last_error}). If it keeps happening,
          tell whoever administers HolidayHive.
        </p>
      )}
      {shown.includes('slack') && !settings.slack_found && (
        <div className="flex items-start gap-2 mt-3 p-3 rounded-md bg-amber-50 border border-amber-200"
             data-testid="notification-slack-not-found">
          <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" aria-hidden="true" />
          <p className="text-xs text-amber-800">
            {!settings.slack_connected
              ? 'Your company’s Slack isn’t connected any more, so we can’t message you there. '
              : settings.email
                ? <>We couldn’t find <strong>{settings.email}</strong> in {team}. </>
                : <>We couldn’t find you in {team}. </>}
            {ticks.includes('slack') && emailCovers && 'Until we can, your notifications come by email. '}
            {isAdmin ? (
              <Link to="/settings?tab=integrations" className="underline font-medium"
                    data-testid="notification-slack-setup-link">
                Link yourself by hand in Settings &rarr; Integrations
              </Link>
            ) : (
              'Ask whoever administers HolidayHive to check the email on your profile, or to link your Slack by hand.'
            )}
          </p>
        </div>
      )}

      {/* ── "Was this you?" (3A) ──────────────────────────────────────────────────── */}
      <label className={`flex items-start gap-3 mt-5 pt-4 border-t border-border ${saving ? 'cursor-not-allowed' : 'cursor-pointer'}`}
             data-testid="notification-new-device">
        <input
          type="checkbox"
          className="mt-1 h-4 w-4 accent-primary"
          checked={settings.new_device_alerts !== false}
          disabled={saving}
          onChange={toggleNewDevice}
          data-testid="notification-new-device-box"
        />
        <span className="min-w-0">
          <span className="text-sm font-medium">Tell me when I sign in on a new device</span>
          <span className="block text-xs text-muted-foreground mt-0.5 max-w-md">
            A &ldquo;Was this you?&rdquo; message, so you&rsquo;d know if somebody else got into
            your account. We recommend leaving this on.
          </span>
        </span>
      </label>
    </div>
  );
}
